import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useCallback,
  useRef,
} from "react";
import type { ForwardRefRenderFunction } from "react";
import classnames from "classnames";
import { Header, Wrapper } from "@/components";
import { i18n } from "@/utils";

import Icon from "./icon";
import styles from "./style.less";
import { ColorOption, ColorVariant } from "./mini-me";
import cloneDeep from "lodash.clonedeep";

export interface IProps {
  /**
   * @description 值
   */
  value: ColorVariant[];
  /**
   * @description 关闭回调
   */
  onClose: () => void;
  /**
   * @description 确认回调
   */
  onConfirm: (values: ColorOption[]) => void;
}

export interface IHandle {
  /**
   * @description 确认回调
   */
  confirm: () => ColorOption[];
}

const Radio: ForwardRefRenderFunction<IHandle, IProps> = (
  { value, onClose, onConfirm },
  ref
) => {
  /**
   * 当前的值
   * 如果设置了必填校验 默认值不强制设置 否则为options[0]
   */
  const [values, setValues] = useState<ColorOption[]>([]);
  /** 错误 */
  const [errors, setErrors] = useState<string[]>([]);
  /** 内容区域 */
  const _content = useRef<HTMLDivElement>(null);
  /** 头部阴影 */
  const [headerShadow, setHeaderShadow] = useState(false);

  /** 监听content滚动 */
  const handleContentScrollCapture = () => {
    if (_content.current) {
      if (_content.current.scrollTop > 60 && !headerShadow)
        setHeaderShadow(true);
      if (_content.current.scrollTop < 60 && headerShadow)
        setHeaderShadow(false);
    }
  };

  useEffect(() => {
    setValues(Array.from({ length: value.length }));
  }, [value]);

  useImperativeHandle(ref, () => ({
    confirm: () => {
      return values;
    },
  }));

  /** 值改变监听 */
  const handleChange = useCallback(
    (_value: ColorOption, index: number) => {
      values[index] = _value;
      setValues(cloneDeep(values));
    },
    [values]
  );

  /** 刻字输入 */
  const handleChangeText = (
    option: ColorOption,
    text: string,
    itemIndex: number
  ) => {
    option.engrave = text;
    values[itemIndex] = option;
    setValues(cloneDeep(values));
  };

  /** 点击确认 */
  const handleConfirm = () => {
    onConfirm(values);
  };

  /** 渲染选项 */
  const renderOption = (
    item: ColorVariant,
    itemIndex: number,
    option: ColorOption
  ) => {
    /** 是否选中 */
    const selected =
      values.filter((val) => val && val.id === option.id).length > 0;

    return (
      <div
        className={classnames(styles.optionWrapper, {
          [styles.active]: selected,
        })}
        key={option.id}
      >
        <div
          className={styles.imageWrapper}
          onClick={() => handleChange(option, itemIndex)}
        >
          {selected && (
            <div className={styles.selectedIcon}>
              <Icon type="tick-circle" color="#ff533a" size={16} />
            </div>
          )}
          <div
            className={styles.img}
            style={{
              backgroundImage: `url(${option.image})`,
            }}
          ></div>
          {/* <img src={option.image} alt="" /> */}
        </div>
        {item.showLabel && (
          <div className={styles.labelWrapper}>
            {option.label}
            {option.price && <b>+{option.price}</b>}
          </div>
        )}
      </div>
    );
  };

  /** 渲染内容 */
  const renderContent = (item: ColorVariant, itemIndex: number) => {
    switch (item.type) {
      case "radio":
        return (
          <div
            className={classnames(styles.itemContent, styles.radioWrapper)}
            style={{
              gridTemplateColumns: `repeat(${item.column},1fr)`,
            }}
          >
            {item.options.map((option) =>
              renderOption(item, itemIndex, option)
            )}
          </div>
        );
      case "text":
        // 是否显示刻字
        let engraveOpt = values[itemIndex];
        return (
          <div
            key={item.id}
            className={classnames(styles.itemContent, styles.textWrapper)}
          >
            <div
              className={styles.textWrapperContent}
              style={{
                gridTemplateColumns: `repeat(${item.column},1fr)`,
              }}
            >
              {item.options.map((option) =>
                renderOption(item, itemIndex, option)
              )}
            </div>
            {engraveOpt && "engrave" in engraveOpt && (
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  placeholder={engraveOpt.placeholder}
                  className={styles.input}
                  maxLength={engraveOpt.maxLength}
                  onChange={(e) =>
                    handleChangeText(engraveOpt, e.target.value, itemIndex)
                  }
                />
                <span className={styles.suffix}>
                  {(engraveOpt as ColorOption).engrave?.length}/{" "}
                  {engraveOpt.maxLength}
                </span>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Wrapper className={classnames(styles.colorOptions, styles.radio)}>
      <Header
        className={classnames(styles.colorOptionsHeader, {
          [styles.headerShadow]: headerShadow,
        })}
        closeIconType="arrow-left"
        closeIconColor="#000"
        onClose={onClose}
        renderContent={
          <div className={styles.titleWrapper}>
            <div className={styles.title}>
              {i18n.format("modules.mini.me.Personalization")}
            </div>
            <div className={styles.subTitle}>
              {i18n.format("modules.mini.me.Personalization")}
            </div>
          </div>
        }
        renderSubmit={
          <div
            className={classnames(styles.submit, {
              [styles.disabled]: values.some((item) => {
                if (item && "engrave" in item) {
                  return !Boolean(item.engrave);
                }
                return !Boolean(item);
              }),
            })}
            onClick={handleConfirm}
          >
            {i18n.format("modules.global.confirm")}
          </div>
        }
      />
      <div
        className={styles.content}
        ref={_content}
        onScrollCapture={handleContentScrollCapture}
      >
        {value.map((item, itemIndex) => (
          <div className={styles.itemWrapper} key={item.id}>
            <div className={styles.itemTitle}>{item.title}</div>

            {renderContent(item, itemIndex)}
          </div>
        ))}
      </div>
    </Wrapper>
  );
};

export default forwardRef(Radio);
